import { memo } from 'react';
import { getUser } from '@frontend/auth-helpers';
import { i18next, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Avatar, Text, Truncated, useTooltip } from '@frontend/design-system';
import { teamPresenceTrackingId } from '../../pendo-tracking';
import { CardBody, DraggableCard, DurationComponent, LocationChip, SelectableCard } from './atoms';
import { TourHighlight } from './tour-highlight';
import { DeviceCardProps, DeviceSelectableCardProps, CardProps } from './types';
import { getSearchedNameParts } from './utils';

export const DeviceNameplate = ({
  name = i18next.t('Unknown'),
  subtitle = i18next.t('Unknown'),
  searchTerm,
}: {
  name: string;
  subtitle: string;
  searchTerm?: string;
}) => {
  let _name = (
    <Truncated as={Text} weight='bold'>
      {name}
    </Truncated>
  );
  let _subtitle = (
    <Truncated as={Text} color='subdued' size='medium'>
      {subtitle}
    </Truncated>
  );

  if (searchTerm) {
    if (name.toLowerCase().includes(searchTerm.toLowerCase())) {
      const nameParts = getSearchedNameParts(name, searchTerm);

      _name = (
        <Truncated as={Text} weight='bold'>
          {nameParts[0]}
          <Text
            as='span'
            weight='bold'
            css={{
              backgroundColor: theme.colors.primary50,
              color: theme.colors.white,
              borderRadius: theme.borderRadius.small,
            }}
          >
            {nameParts[1]}
          </Text>
          {nameParts[2]}
        </Truncated>
      );
    }

    if (subtitle.toLowerCase().includes(searchTerm.toLowerCase())) {
      const subtitleParts = getSearchedNameParts(subtitle, searchTerm);

      _subtitle = (
        <Truncated as={Text} color='subdued' size='medium'>
          {subtitleParts[0]}
          <Text
            as='span'
            css={{
              backgroundColor: theme.colors.primary50,
              color: theme.colors.white,
              borderRadius: theme.borderRadius.small,
            }}
          >
            {subtitleParts[1]}
          </Text>
          {subtitleParts[2]}
        </Truncated>
      );
    }
  }

  return (
    <>
      {_name}
      {_subtitle}
    </>
  );
};

export const DeviceAvatar = ({ id }: { id: string }) => {
  const { t } = useTranslation('calls');
  return (
    <TourHighlight
      dotId={`device-avatar-${id}`}
      message={t('This is a monitored Device.')}
      placement='top'
      dotPosition={{ top: 1, right: 1 }}
    >
      <Avatar iconName='cell-phone' />
    </TourHighlight>
  );
};

export const DeviceCard = memo(function DeviceCard({
  id,
  userId,
  name,
  subtitle,
  locations,
  orgId,
  deviceState,
  userName,
  deviceExtension,
  deviceActivity,
  capabilities = {
    canCall: true,
    canMessage: true,
    canRemove: true,
  },
  searchTerm,
  showLocations,
  onMessageClick,
  onCallClick,
  onRemoveClick,
  onPinClick,
  showUnpinIcon,
}: Omit<DeviceCardProps, 'type'> & { showLocations?: boolean }) {
  const { t } = useTranslation('phone');
  const isWeaveUser = getUser()?.type === 'weave';
  const messageTooltip = () => {
    if (capabilities?.isSelf) {
      return t('Cannot message yourself');
    }

    if (!capabilities?.canMessage) {
      return t('A user must connect to this device before you can chat');
    }

    return t('Message {{name}}', { name: userName });
  };
  const callTooltip = () => {
    if (isWeaveUser) {
      return t("Weave users can't make calls");
    }

    if (capabilities?.isSelf) {
      return t('Cannot call yourself');
    }

    if (!capabilities?.canCall) {
      return t("This user can't receive calls - no device connected");
    }

    return t('Call {{name}}', { name });
  };

  const actions: CardProps['actions'] = [
    ...(!!capabilities?.hasTeamChatAccess
      ? [
          {
            icon: 'message' as const,
            label: t('Message'),
            onClick: () => {
              onMessageClick?.(userId ?? '', userName ?? '', orgId);
            },
            disabled: !capabilities?.canMessage || !!capabilities?.isSelf,
            tooltip: messageTooltip(),
            trackingId: teamPresenceTrackingId({ context: 'device-card::message' }),
          },
        ]
      : []),
    {
      icon: 'phone' as const,
      label: t('Call'),
      onClick: () => {
        onCallClick?.(deviceExtension);
      },
      tooltip: callTooltip(),
      callout: t('Click here to call a User or Device (not available in the tour).'),
      disabled: !capabilities?.canCall || !!capabilities?.isSelf || isWeaveUser,
      trackingId: teamPresenceTrackingId({ context: 'device-card::call' }),
    },
    {
      icon: 'remove2' as const,
      label: t('Remove'),
      onClick: () => {
        onRemoveClick?.(id, name);
      },
      tooltip: t('Remove {{name}}', { name }),
      disabled: !capabilities?.canRemove,
      trackingId: teamPresenceTrackingId({ context: 'device-card::remove' }),
    },
    {
      icon: showUnpinIcon ? ('pin-filled' as const) : ('pin' as const),
      label: showUnpinIcon ? t('Unpin from Top') : t('Pin to Top'),
      onClick: () => {
        onPinClick?.(id, name);
      },
      disabled: false,
      tooltip: showUnpinIcon ? t('Unpin from Top') : t('Pin to Top'),
      callout: t('Click here to pin a user or device for quick visibility.'),
      trackingId: teamPresenceTrackingId({
        context: showUnpinIcon ? 'device-card::unpin-icon' : 'device-card::pin-icon',
      }),
    },
  ];

  const card = (
    <DraggableCard
      id={id}
      nameplate={<DeviceNameplate name={name} subtitle={subtitle} searchTerm={searchTerm} />}
      avatar={<DeviceAvatar id={id} />}
      actions={actions}
      body={
        <CardBody
          cardId={id}
          deviceState={deviceState}
          locations={locations}
          showLocations={showLocations}
          tooltip={t('This shows a Device that is actively on a call with the duration of the call seen below it.')}
        />
      }
      meta={<DeviceCardMeta id={id} deviceActivity={deviceActivity} userName={userName} />}
    />
  );

  return card;
});

const DeviceCardMeta = ({
  id,
  deviceActivity,
  userName,
}: {
  id: string;
  deviceActivity: DeviceCardProps['deviceActivity'];
  userName?: string;
}) => {
  const { t } = useTranslation('phone');
  const hasConnectedUser = !!userName;
  const { triggerProps, tooltipProps, Tooltip } = useTooltip({ trigger: 'hover' });

  return (
    <div
      css={{
        display: 'grid',
        rowGap: theme.spacing(1),
        gridTemplateAreas: '"time" "device"',
        gridTemplateRows: '1fr 1fr',
      }}
    >
      {deviceActivity && <DurationComponent deviceActivity={deviceActivity} />}
      <div
        css={{
          gridArea: 'device',
          display: 'grid',
          gridTemplateColumns: `auto 1fr`,
          gap: theme.spacing(1),
          alignItems: 'center',
        }}
      >
        {hasConnectedUser ? (
          <TourHighlight
            dotId={`device-user-${id}`}
            message={t('This is the User that is currently connected to this Device.')}
            placement='top'
            containerCss={{ display: 'inline-flex' }}
            dotPosition={{ top: -2, right: -5 }}
          >
            <div
              css={{
                display: 'grid',
                gridTemplateColumns: `auto 1fr`,
                gap: theme.spacing(1),
                alignItems: 'center',
              }}
            >
              <div {...triggerProps}>
                <Icon name='user-small' color='light' />
              </div>
              <Text size='medium'>{hasConnectedUser ? userName : t('NO USER CONNECTED')}</Text>
              <Tooltip {...tooltipProps}>{t('Connected User')}</Tooltip>
            </div>
          </TourHighlight>
        ) : (
          <>
            <Icon name='user-small' color='light' />
            <Text size='medium'>{hasConnectedUser ? userName : t('NO USER CONNECTED')}</Text>
          </>
        )}
      </div>
    </div>
  );
};

export const SelectableDeviceCard = ({
  id,
  name,
  subtitle,
  searchTerm,
  locations,
  onChange,
  isSelected,
  disabled,
  disabledTooltip,
  showLocations,
}: DeviceSelectableCardProps & {
  showLocations?: boolean;
  disabledTooltip?: string;
}) => {
  return (
    <SelectableCard
      avatar={<DeviceAvatar id={id} />}
      nameplate={<DeviceNameplate name={name} subtitle={subtitle} searchTerm={searchTerm} />}
      meta={showLocations ? <LocationChip locations={locations} /> : null}
      onChange={onChange}
      isSelected={isSelected}
      disabled={disabled}
      disabledTooltip={disabledTooltip}
    />
  );
};
